/**
 * VelvetGate — Ticket Display State Resolver (iter130)
 *
 * Single source of truth for USER-FACING ticket lifecycle labels.
 *
 * Why this exists:
 *   Several surfaces (My Passes, TicketDisplay, future analytics) need to
 *   render a "what is this pass right now?" status. Previously each surface
 *   resolved its own status from `ticket.status` directly — which meant a
 *   ticket stuck in `pending_approval` after the event ended still showed
 *   "PENDING" to the guest, which is misleading. This helper introduces
 *   COMPUTED display states (e.g. REQUEST EXPIRED) without mutating any
 *   stored data, so reports, analytics, and audit trails are untouched.
 *
 * Contract:
 *   getTicketDisplayState(ticket, event?, now?) → {
 *     key:   stable machine identifier (use for testids / branching)
 *     label: human-facing display string (use in UI)
 *     tone:  semantic color tone (granted / pending / archive / warning / revoked)
 *     icon:  lucide-react icon name (use to render <Icon />)
 *   }
 *
 *   `event` is optional. If omitted we fall back to `ticket.event_date` /
 *   `ticket.event_end_time` fields that the backend hydrates on the
 *   /my-tickets list. Passing the full event object is preferred when
 *   available (e.g. on TicketDisplay).
 *
 *   `now` is injected for testability; defaults to `new Date()`.
 *
 * Resolution order is intentional — terminal/explicit states win over
 * derived/inferred ones. Do not reorder without updating the test matrix:
 *   1.  revoked            → REVOKED (host action, terminal)
 *   2.  used               → CHECKED IN (door scan, terminal)
 *   3.  cancelled (RSVP)   → CANCELLED (guest action, terminal)
 *   4.  rejected/declined  → DECLINED (host action, terminal)
 *   5.  pending + past     → REQUEST EXPIRED (new; was misleadingly "PENDING")
 *   6.  pending + future   → UNDER REVIEW
 *   7.  RSVP no-show       → DID NOT ATTEND (only after event end)
 *   8.  cash unpaid + past → PAYMENT NOT COMPLETED
 *   9.  active + past      → EXPIRED
 *   10. default            → ACCESS GRANTED
 *
 * NOT changing:
 *   - DB ticket.status
 *   - reports/analytics aggregation (those read raw ticket.status)
 *   - audit logs
 *   - check-in scan logic
 */

// Semantic tone → Tailwind class bundles. Muted/archival for inactive
// states per spec — never overly colorful.
const TONES = {
  granted: {
    chip: 'bg-[#C6A66B]/10 text-[#C6A66B] border-[#C6A66B]/30',
    badge: 'bg-[#C6A66B]/10 border-[#C6A66B]/25 text-[#C6A66B]',
    accent: 'bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A]',
  },
  used: {
    chip: 'bg-[#3A6DA0]/15 text-[#7BAEDB] border-[#3A6DA0]/30',
    badge: 'bg-[#3A6DA0]/10 border-[#3A6DA0]/25 text-[#7BAEDB]',
    accent: 'bg-[#3A6DA0]',
  },
  pending: {
    chip: 'bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/25',
    badge: 'bg-[#FFB800]/10 border-[#FFB800]/25 text-[#FFB800]',
    accent: 'bg-[#FFB800]',
  },
  warning: {
    // For cash-unpaid / payment-not-completed — slightly stronger than
    // archive but still muted, never red.
    chip: 'bg-[#A88438]/10 text-[#C9A35E] border-[#A88438]/30',
    badge: 'bg-[#A88438]/10 border-[#A88438]/30 text-[#C9A35E]',
    accent: 'bg-[#A88438]',
  },
  archive: {
    // EXPIRED / REQUEST EXPIRED / DID NOT ATTEND / CANCELLED — muted,
    // archival, never alarming.
    chip: 'bg-[#1F1F1F] text-[#8A8A8A] border-[#2A2A2A]',
    badge: 'bg-[#0F0F0F] border-[#2A2A2A] text-[#8A8A8A]',
    accent: 'bg-[#2A2A2A]',
  },
  declined: {
    chip: 'bg-[#5A2A2A]/30 text-[#C99090] border-[#5A2A2A]/60',
    badge: 'bg-[#5A2A2A]/20 border-[#5A2A2A]/60 text-[#C99090]',
    accent: 'bg-[#5A2A2A]',
  },
  revoked: {
    chip: 'bg-[#C44545]/15 text-[#E07070] border-[#C44545]/40',
    badge: 'bg-[#C44545]/15 border-[#C44545] text-[#C44545]',
    accent: 'bg-[#C44545]',
  },
};

/** Lower-cased event-end resolver. Returns a Date or null. */
function _resolveEventEnd(ticket, event) {
  // Prefer event payload if present (TicketDisplay path).
  const date = event?.date || ticket?.event_date;
  if (!date) return null;
  // event.end_time may be null for a list endpoint. Fall back to event.time
  // + 6h, mirroring ManageEvent's eventStatus derivation.
  const endTime = event?.end_time || ticket?.event_end_time;
  const startTime = event?.time || ticket?.event_time || '00:00';
  try {
    const start = new Date(`${date}T${startTime}`);
    if (endTime) {
      // Overnight events: end_time < start_time → +1 day.
      const end = new Date(`${date}T${endTime}`);
      if (end < start) end.setDate(end.getDate() + 1);
      return end;
    }
    return new Date(start.getTime() + 6 * 60 * 60 * 1000);
  } catch {
    return null;
  }
}

function _eventEnded(ticket, event, now) {
  const end = _resolveEventEnd(ticket, event);
  return end ? end < now : false;
}

export function getTicketDisplayState(ticket, event = null, now = new Date()) {
  // ── 1. REVOKED — host action, terminal ──────────────────────────────
  if (ticket?.status === 'revoked') {
    return { key: 'revoked', label: 'Revoked', tone: 'revoked', icon: 'Shield', ...TONES.revoked };
  }

  // ── 2. CHECKED IN — door scan, terminal ─────────────────────────────
  if (ticket?.status === 'used') {
    return { key: 'used', label: 'Checked In', tone: 'used', icon: 'Eye', ...TONES.used };
  }

  // ── 3. RSVP CANCELLED — guest self-cancel, terminal ─────────────────
  if (ticket?.rsvp_status === 'cancelled') {
    return { key: 'cancelled', label: 'RSVP Cancelled', tone: 'archive', icon: 'XCircle', ...TONES.archive };
  }

  // ── 4. DECLINED — host rejection, terminal ──────────────────────────
  if (ticket?.status === 'rejected' || ticket?.status === 'declined') {
    return { key: 'declined', label: 'Declined', tone: 'declined', icon: 'XCircle', ...TONES.declined };
  }

  const eventEnded = _eventEnded(ticket, event, now);

  // ── 5. PENDING + past event — REQUEST EXPIRED (new lifecycle state) ─
  if (ticket?.status === 'pending_approval' && eventEnded) {
    return { key: 'request_expired', label: 'Request Expired', tone: 'archive', icon: 'Clock', ...TONES.archive };
  }

  // ── 6. PENDING + future — UNDER REVIEW ──────────────────────────────
  if (ticket?.status === 'pending_approval') {
    return { key: 'pending', label: 'Under Review', tone: 'pending', icon: 'Clock', ...TONES.pending };
  }

  // ── 7. RSVP no-show — only meaningful after event end ───────────────
  if (ticket?.rsvp_status === 'no_show' && eventEnded) {
    return { key: 'no_show', label: 'Did Not Attend', tone: 'archive', icon: 'AlertCircle', ...TONES.archive };
  }

  // ── 8. Cash RSVP unpaid after event ended — PAYMENT NOT COMPLETED ───
  // We only check this on past events because future cash RSVPs are
  // legitimately "pay-at-door" until then.
  const isCashUnpaid =
    ticket?.payment_mode === 'cash' &&
    !ticket?.payment_confirmed &&
    parseFloat(ticket?.expected_amount || 0) > 0;
  if (isCashUnpaid && eventEnded) {
    return { key: 'payment_not_completed', label: 'Payment Not Completed', tone: 'warning', icon: 'AlertCircle', ...TONES.warning };
  }

  // ── 9. Generic past event with an otherwise-active pass — EXPIRED ───
  if (eventEnded) {
    return { key: 'expired', label: 'Expired', tone: 'archive', icon: 'AlertCircle', ...TONES.archive };
  }

  // ── 10. Default — ACCESS GRANTED ────────────────────────────────────
  return { key: 'granted', label: 'Access Granted', tone: 'granted', icon: 'CheckCircle', ...TONES.granted };
}

/**
 * Role-pill display mapping. Single source so we don't drift between
 * surfaces.
 */
export function getUserRoleLabel(role) {
  switch (role) {
    case 'admin': return { label: 'Platform Admin', cls: 'bg-[#D4AF37]/12 text-[#D4AF37] border-[#D4AF37]/30' };
    case 'host':  return { label: 'Host',           cls: 'bg-[#C6A66B]/10 text-[#C6A66B] border-[#C6A66B]/25' };
    case 'staff': return { label: 'Event Staff',    cls: 'bg-[#7BAEDB]/10 text-[#7BAEDB] border-[#7BAEDB]/25' };
    default:      return { label: 'Member',         cls: 'bg-[#2A2A2A] text-[#AFAFAF] border-[#3A3A3A]' };
  }
}
